import React from "react";
import PropTypes from 'prop-types';
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {RadioButton} from "primereact/radiobutton";
import GeneralUtils from "../../utils/GeneralUtils";

const MANY_ITEMS_THRESHOLD = 4;
/** Use -1 so that item id 0 is never treated as "None" (fixes selection becoming none when first item has id 0). */
export const CONFLICT_SELECT_NONE = -1;

export default class PurchaseOrderItemConflictDialog extends React.Component {

    static propTypes = {
        onSaveClick: PropTypes.func,
        onCancelClick: PropTypes.func,
        show: PropTypes.bool,
        conflictingItems: PropTypes.array,
        message: PropTypes.string
    };

    constructor(props) {
        super(props);
        this.state = {
            selectedIndex: 0
        };
        this.selectedIndexRef = React.createRef();
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && !prevProps.show && this.props.conflictingItems && this.props.conflictingItems.length > 0) {
            this.setState({ selectedIndex: 0 });
            this.selectedIndexRef.current = 0;
        }
    }

    /** Selection by row index so first row is always first item, last row is always None (no id -1 confusion). */
    handleRowClick = (e) => {
        e.stopPropagation();
        const raw = e.currentTarget.getAttribute('data-item-index');
        if (raw === null) return;
        const index = parseInt(raw, 10);
        const list = this._displayItems;
        if (!list || index < 0 || index >= list.length) return;
        this.selectedIndexRef.current = index;
        this.setState({ selectedIndex: index });
    };

    handleSave = () => {
        const list = this._displayItems;
        const refIdx = this.selectedIndexRef.current;
        const stateIdx = this.state.selectedIndex;
        const index = refIdx !== undefined && refIdx !== null ? refIdx : stateIdx;
        if (list == null || index == null || index < 0 || index >= list.length) return;
        const item = list[index];
        const itemId = item.id;
        if (itemId === CONFLICT_SELECT_NONE) {
            this.props.onSaveClick(CONFLICT_SELECT_NONE);
        } else {
            this.props.onSaveClick(typeof itemId === 'string' ? Number(itemId) : itemId);
        }
    };

    /** Stops event from reaching select; on mobile the combo can steal the tap otherwise. */
    onSaveClick = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        this.handleSave();
    };

    render() {
        const { show, conflictingItems, message, onCancelClick } = this.props;
        const { selectedIndex } = this.state;
        const isMobile = GeneralUtils.isMobile();
        const count = conflictingItems ? conflictingItems.length : 0;
        const manyItems = count >= MANY_ITEMS_THRESHOLD;
        const useFullscreen = isMobile && manyItems;

        if (!conflictingItems || count === 0) {
            return null;
        }

        const displayItems = [...conflictingItems, { id: CONFLICT_SELECT_NONE, isNone: true }];
        this._displayItems = displayItems;
        const itemPadding = isMobile ? '10px' : '12px';
        const itemMarginBottom = isMobile ? '6px' : '8px';
        const fontSize = isMobile ? '14px' : '15px';
        const detailFontSize = isMobile ? '11px' : '12px';
        const listMaxHeight = useFullscreen ? 'none' : (manyItems ? (isMobile ? '50vh' : '55vh') : (isMobile ? '280px' : '320px'));

        const modalStyle = useFullscreen
            ? undefined
            : { maxWidth: isMobile ? '92vw' : '560px', width: '100%' };

        return (
            <Modal
                isOpen={show}
                toggle={onCancelClick}
                fullscreen={useFullscreen}
                centered
                style={modalStyle}
            >
                <ModalHeader toggle={onCancelClick}>
                    Conflict Resolution Required
                </ModalHeader>
                <ModalBody style={{ padding: isMobile ? '12px 16px' : '16px 20px' }}>
                    <p style={{ marginBottom: '12px', fontSize: '14px', color: '#6c757d', lineHeight: 1.5 }}>
                        {message || "These items conflict with the same barcode but different product codes. Please select one to keep."}
                    </p>
                    {isMobile ? (
                        <>
                            <select
                                className="form-control"
                                value={String(selectedIndex)}
                                onChange={(e) => {
                                    const i = parseInt(e.target.value, 10);
                                    if (!Number.isNaN(i) && i >= 0) {
                                        this.selectedIndexRef.current = i;
                                        this.setState({ selectedIndex: i });
                                    }
                                }}
                                style={{ width: '100%', fontSize: '16px', padding: '10px 12px', marginBottom: '16px', touchAction: 'manipulation' }}
                                aria-label="Select item to keep"
                            >
                                {displayItems.map((item, index) => {
                                    const isNone = item.id === CONFLICT_SELECT_NONE || item.isNone;
                                    const label = isNone
                                        ? 'None of them'
                                        : (item.orjDescription || item.itemName || 'Item #' + (index + 1)) + (item.barcode ? ' (' + item.barcode + ')' : '');
                                    return (
                                        <option key={item.isNone ? 'none' : (item.id || index)} value={String(index)}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                            <div style={{ marginTop: '8px' }}>
                                <Button color="primary" type="button" block onClick={this.onSaveClick} style={{ minHeight: '48px', fontSize: '16px', touchAction: 'manipulation' }}>
                                    Save
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div
                            style={{
                                maxHeight: listMaxHeight,
                                overflowY: 'auto',
                                paddingRight: '4px'
                            }}
                        >
                            {displayItems.map((item, index) => {
                                const isNone = item.id === CONFLICT_SELECT_NONE || item.isNone;
                                const isSelected = selectedIndex === index;
                                return (
                                    <div
                                        key={item.isNone ? 'none' : (item.id || index)}
                                        data-item-index={index}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleRowClick(e); } }}
                                        style={{
                                            padding: itemPadding,
                                            marginBottom: itemMarginBottom,
                                            border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#e7f3ff' : '#fff'
                                        }}
                                        onClick={this.handleRowClick}
                                        onMouseEnter={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = '#fff';
                                            }
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                            <div style={{ marginTop: '2px', flexShrink: 0 }}>
                                                <RadioButton
                                                    inputId={`item-${item.isNone ? 'none' : item.id}`}
                                                    name="conflictItem"
                                                    value={item.id}
                                                    checked={isSelected}
                                                    readOnly
                                                />
                                            </div>
                                            <div style={{ cursor: 'pointer', flex: 1, marginBottom: 0, width: '100%' }}
                                            >
                                                <div style={{
                                                    fontWeight: '600',
                                                    marginBottom: isNone ? 0 : '4px',
                                                    fontSize,
                                                    color: '#212529',
                                                    lineHeight: 1.4,
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {isNone ? 'None of them' : (item.orjDescription || item.itemName || 'Item #' + (index + 1))}
                                                </div>
                                                {!isNone && (
                                                    <div style={{
                                                        fontSize: detailFontSize,
                                                        color: '#6c757d',
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: '8px',
                                                        alignItems: 'center'
                                                    }}>
                                                        {item.barcode && (
                                                            <span>
                                                                <span style={{ fontWeight: '500' }}>Barcode:</span> {item.barcode}
                                                            </span>
                                                        )}
                                                        {item.productCode && (
                                                            <span>
                                                                <span style={{ fontWeight: '500' }}>Product Code:</span> {item.productCode}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter style={{ padding: isMobile ? '16px' : '16px 20px', gap: '10px', flexWrap: 'wrap', position: 'relative', zIndex: 10 }}>
                    {onCancelClick && (
                        <Button color="secondary" onClick={onCancelClick} style={isMobile ? { minHeight: '48px', padding: '12px 24px', fontSize: '16px' } : undefined}>
                            Cancel
                        </Button>
                    )}
                    {!isMobile && (
                        <Button color="primary" type="button" onClick={this.onSaveClick}>
                            Save
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        );
    }
}
